import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, Button } from '@mui/material';
import AutoCompleteField from '../../AutoCompleteField/AutoCompleteField';
import isEmpty from '../../../../is-empty';

const ScreenDef = (props) => {
    const { settingsData, callBack, toggleDialog } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [state, setState] = useState({
        _id: '',
        name: '',
        path: '',
        object: '',
        objSel: {},
        objects: [],
        category: '',
        catSel: {},
        categories: [],
        group: '',
        groupSel: {},
        groups: [],
        tags: [],
        tagSels: [],
        tagOptions: [],
        enableSave: true
    });

    const loadPropRecord = useCallback(() => {
        const objSel = settingsData.objects.find(x => x.value === settingsData.object);
        const catSel = settingsData.categories.find(x => x.value === settingsData.category);
        const groupSel = settingsData.groups.find(x => x.value === settingsData.group);
        const tagOptions = settingsData.tagOptions || [];
        const currentTagIds = (settingsData.tags || []).map(t => String(t));
        const tagSels = tagOptions.filter(o => currentTagIds.includes(String(o.value)));
        setState({
            _id: settingsData._id,
            name: settingsData.name,
            path: settingsData.path,
            object: settingsData.object,
            objSel: objSel,
            objects: settingsData.objects,
            category: settingsData.category,
            catSel: catSel,
            categories: settingsData.categories,
            group: settingsData.group,
            groupSel: groupSel,
            groups: settingsData.groups,
            tags: currentTagIds,
            tagSels,
            tagOptions,
            enableSave: true
        });
    }, [settingsData]);

    useEffect(() => {
        if (!isEmpty(settingsData)) {
            loadPropRecord();
        }
    }, [settingsData, loadPropRecord]);

    const changeAuto = useCallback((name, data) => (e, newValue) => {
        setState(prevState => ({
            ...prevState,
            [name]: newValue,
            [data]: newValue != null ? newValue.value : null
        }));
    }, []);

    const changeTags = useCallback((e, newValue) => {
        setState(prevState => ({
            ...prevState,
            tagSels: newValue || [],
            tags: (newValue || []).map(v => v.value)
        }));
    }, []);

    const createTag = useCallback(async (inputValue, existingSelected) => {
        try {
            const res = await axios.post('/int/helplink/tag', { label: inputValue });
            const newTag = res.data;
            setState(prevState => {
                const base = existingSelected != null ? existingSelected : prevState.tagSels;
                const already = base.some(v => String(v.value) === String(newTag.value));
                const nextSels = already ? base : [...base, newTag];
                const nextOptions = prevState.tagOptions.some(o => String(o.value) === String(newTag.value))
                    ? prevState.tagOptions
                    : [...prevState.tagOptions, newTag];
                return {
                    ...prevState,
                    tagSels: nextSels,
                    tags: nextSels.map(v => v.value),
                    tagOptions: nextOptions
                };
            });
        } catch (err) {
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response?.data || err.message, title: t('error'), severity: 'error' } });
        }
    }, [dispatch, t]);

    const changeValue = useCallback((e) => {
        const { name, value } = e.target;
        setState(prevState => ({
            ...prevState,
            [name]: value
        }));
    }, []);

    const _save = useCallback(async () => {
        const data = {
            _id: state._id,
            name: state.name,
            object: state.object,
            category: state.category,
            group: state.group,
            path: state.path,
            tags: state.tags
        };
        try {
            const result = await axios.post('/int/screendefinition', data);
            setState(prevState => ({ ...prevState, enableSave: true }));
            callBack();
            dispatch({ type: 'CREATE_ALERT', payload: { message: result.data.message, title: t('success'), severity: 'success' } });
        } catch (err) {
            setState(prevState => ({ ...prevState, enableSave: true }));
            dispatch({ type: 'CREATE_ALERT', payload: { message: err.response.data, title: t('error'), severity: 'error' } });
        }
    }, [state._id, state.name, state.object, state.category, state.group, state.path, state.tags, callBack, dispatch, t]);

    const save = useCallback(() => {
        if (!state.enableSave) return;
        setState(prevState => ({ ...prevState, enableSave: false }));
        _save();
    }, [state.enableSave, _save]);

    const { name, path, objSel, objects, catSel, categories, groupSel, groups, tagSels, tagOptions } = state;

    return (
        <Grid container spacing={2}>
            <Grid container spacing={2} size={12}>
                <Grid size={10}>
                    <TextField
                        value={name || ''}
                        onChange={changeValue}
                        name="name"
                        size="medium"
                        fullWidth={true}
                        label="Name"
                    />
                </Grid>
                <Grid size={6}>
                    <TextField
                        value={path || ''}
                        onChange={changeValue}
                        name="path"
                        size="medium"
                        fullWidth={true}
                        label="Path"
                    />
                </Grid>
                <Grid size={6}>
                    <AutoCompleteField
                        value={objSel || ''}
                        options={objects || []}
                        onChange={changeAuto('objSel', 'object')}
                        label="Object"
                    />
                </Grid>
                <Grid size={6}>
                    <AutoCompleteField
                        value={catSel || ''}
                        options={categories || []}
                        onChange={changeAuto('catSel', 'category')}
                        label="Category"
                    />
                </Grid>
                <Grid size={6}>
                    <AutoCompleteField
                        value={groupSel || ''}
                        options={groups || []}
                        onChange={changeAuto('groupSel', 'group')}
                        label="Group"
                    />
                </Grid>
                <Grid size={12}>
                    <AutoCompleteField
                        chipInput
                        value={tagSels || []}
                        options={tagOptions || []}
                        onChange={changeTags}
                        createOption={createTag}
                        label={t('tags')}
                    />
                </Grid>
            </Grid>
            <Grid container sx={{ justifyContent: 'flex-end' }} size={12}>
                <Grid>
                    <Button onClick={toggleDialog} variant='text' color='secondary' key='screen-settings-close-btn'>Close</Button>
                    <Button
                        onClick={save}
                        variant='text'
                        color='primary'
                        disabled={!state.enableSave}
                        key='screen-settings-submit-btn'
                    >
                        {t('save')}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ScreenDef;
